/**
 * Port von `Client/Components/Forms/IwmFormContainer.razor`, seit REQ-27 als Inhalt einer
 * Schublade: Die 25 Felder stehen auf vier Reitern (REQ-27 §2.2), beim Bearbeiten kommen die
 * lesenden Reiter „Positionen" und „Rechnungen" dazu.
 */

import { Fragment, useState } from 'react';

import { containerApi } from '@/api/services/containerApi';
import { TabPanel, Tabs, type TabItem } from '@/components/ui';
import { createContainer, type Container } from '@/types/entities';
import { EntityTypes } from '@/types/enums';

import { ContainerPositions } from './ContainerPositions';
import { ContainerReceipts } from './ContainerReceipts';
import { containerFieldRenderers, containerTabs, type ContainerTab } from './containerFields';
import { FormFrame, type FormShell } from './FormFrame';
import { FormSection } from './FormSection';
import { containerRequiredRules } from './requiredFields';
import { useRelatedList } from './useRelatedList';
import { useViewForm, type ViewForm } from './useViewForm';

export interface ContainerFormProps {
  id: number;
  onSave(id: number): void;
  onCancel(): void;
  /** Hülle um Rumpf und Fußleiste — die Seitenleiste steckt beides in ihre `Drawer`. */
  shell: FormShell;
}

const TABS_ID = 'container-form';
const POSITIONS_TAB = 'positionen';
const RECEIPTS_TAB = 'rechnungen';
/** Die beiden lesenden Reiter gibt es nur beim Bearbeiten. */
const RELATED_TABS = [POSITIONS_TAB, RECEIPTS_TAB];

/** Ein Reiter erscheint nur, wenn wenigstens eines seiner Felder in der View steht. */
function isTabVisible(tab: ContainerTab, form: ViewForm<Container>): boolean {
  return tab.fields.some((name) => form.viewField(name) !== null);
}

/** Reiter mit einem sichtbaren Pflichtfeld, das noch nicht gefüllt ist. */
function isTabInvalid(tab: ContainerTab, form: ViewForm<Container>): boolean {
  return tab.fields.some(
    (name) => form.viewField(name) !== null && form.message(name) !== undefined,
  );
}

function buildTitle(id: number, container: Container): string {
  if (id <= 0) return 'Neuer Container';
  const number = container.ContainerNo ?? '';
  return number === '' ? 'Container' : `Container ${number}`;
}

export function ContainerForm(props: ContainerFormProps) {
  const form = useViewForm<Container>({
    id: props.id,
    entity: EntityTypes.Container,
    api: containerApi,
    createEmpty: createContainer,
    rules: containerRequiredRules,
    onSave: (id) => {
      props.onSave(id);
    },
  });

  const [tab, setTab] = useState(containerTabs[0]?.id ?? '');
  // „Refresh" lädt View und Datensatz neu; die beiden lesenden Listen gehören dazu.
  const [refreshes, setRefreshes] = useState(0);
  const editing = props.id > 0;

  const visibleTabs = containerTabs.filter((entry) => isTabVisible(entry, form));
  const invalidTabs = visibleTabs.filter((entry) => isTabInvalid(entry, form));

  // Der gewählte Reiter kann fehlen, solange die View noch lädt oder weil keines seiner
  // Felder in ihr steht; dann gilt der erste vorhandene.
  const ids = [...visibleTabs.map((entry) => entry.id), ...(editing ? RELATED_TABS : [])];
  const active = ids.includes(tab) ? tab : (ids[0] ?? '');

  const listKey = `${String(props.id)}:${String(refreshes)}`;
  const positions = useRelatedList(
    listKey,
    () => containerApi.getOrderPositions(form.viewId, props.id),
    editing && form.ready && active === POSITIONS_TAB,
  );
  const receipts = useRelatedList(
    listKey,
    () => containerApi.getReceiptAssignments(form.viewId, props.id),
    editing && form.ready && active === RECEIPTS_TAB,
  );

  const items: TabItem[] = visibleTabs.map((entry) => ({
    id: entry.id,
    label: entry.label,
    invalid: invalidTabs.includes(entry),
  }));
  if (editing) {
    items.push(
      { id: POSITIONS_TAB, label: 'Positionen', count: positions.count },
      { id: RECEIPTS_TAB, label: 'Rechnungen', count: receipts.count },
    );
  }

  return (
    <FormFrame
      title={buildTitle(props.id, form.model)}
      shell={props.shell}
      ready={form.ready}
      saving={form.saving}
      isValid={form.isValid}
      showRefresh={editing}
      onRefresh={() => {
        form.reload();
        setRefreshes((count) => count + 1);
      }}
      onSave={() => {
        void form.save();
      }}
      onBlockedSave={() => {
        const first = invalidTabs[0];
        if (first !== undefined) setTab(first.id);
      }}
      onCancel={() => {
        props.onCancel();
      }}
    >
      <Tabs
        id={TABS_ID}
        items={items}
        value={active}
        onChange={setTab}
        ariaLabel="Bereiche des Containers"
        stretch
      />
      {visibleTabs.map((entry) => (
        <TabPanel key={entry.id} tabsId={TABS_ID} id={entry.id} active={active === entry.id}>
          <FormSection>
            {entry.fields.map((name) => (
              <Fragment key={name}>{containerFieldRenderers[name](form)}</Fragment>
            ))}
          </FormSection>
        </TabPanel>
      ))}
      {editing ? (
        <>
          <TabPanel tabsId={TABS_ID} id={POSITIONS_TAB} active={active === POSITIONS_TAB}>
            {active === POSITIONS_TAB ? <ContainerPositions list={positions} /> : null}
          </TabPanel>
          <TabPanel tabsId={TABS_ID} id={RECEIPTS_TAB} active={active === RECEIPTS_TAB}>
            {active === RECEIPTS_TAB ? <ContainerReceipts list={receipts} /> : null}
          </TabPanel>
        </>
      ) : null}
    </FormFrame>
  );
}

export default ContainerForm;
